/**
 * EditorSplitPane — draggable vertical splitter for the editor's
 * left (form/raw input) and right (paper preview) panes.
 *
 * Behavior (per pilot review):
 *   - Initial split percent comes from the user's persisted
 *     preference (props.initialPercent), read ONCE on mount.
 *     We don't re-sync on prop changes during a session because
 *     that caused a flash on every drag commit (parent refreshed
 *     auth user → prop changed → state reset → re-layout). The
 *     PATCH /me commit happens fire-and-forget; new-tab persistence
 *     works via a fresh /me fetch on next mount.
 *   - Drag updates the visible position in real time (no flicker).
 *   - Drag clamp: [0, MAX]. While dragging, position rides freely;
 *     on release, anything below COLLAPSE_THRESHOLD snaps to 0 so
 *     the form auto-hides when too narrow to read normally.
 *   - Double-click the handle → always snaps to 0 (paper full-
 *     width), which is the canonical full-screen view.
 *   - At 0%, a re-open tab appears on the left edge ("Form ⟩").
 *     Re-open restores to DEFAULT_OPEN_PERCENT (paper-favored) so
 *     the user lands back in a balance that prioritizes the paper.
 *   - No form-only mode (was right-side collapse). Paper is always
 *     visible; the form is the optional pane.
 *   - Below md (mobile/narrow), splitter is inert and the panes
 *     stack vertically.
 *
 * Storage contract: percent is "left pane share, 0..100". 0 = form
 * collapsed (paper full-width). Practical drag range is [12, MAX].
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ChevronRight, GripVertical } from 'lucide-react';

interface Props {
  /** Initial split percent (0..100). Read once on mount. */
  initialPercent: number;
  /** Called once per drag-end with the new committed percent.
   *  Parent fires PATCH /me fire-and-forget — DOES NOT refresh
   *  the auth user, so our initialPercent prop won't change mid-
   *  session and trigger a flash. */
  onCommit: (percent: number) => void;
  /** Left-pane content (form + raw input). */
  left: ReactNode;
  /** Right-pane content (paper preview). */
  right: ReactNode;
}

/** Imperative handle exposed via ref so parents (e.g. ScaledPaper's
 *  zoom callback) can adjust the splitter geometry in response to
 *  paper-width changes. The splitter owns all layout math (it has
 *  containerRef + the user's preferred percent); callers just
 *  report "the paper now needs N px of right-pane width." */
export interface EditorSplitPaneHandle {
  /** Resize the form pane to make room for a paper of the given
   *  width (px). The splitter:
   *    - Shrinks the form when paper would otherwise overflow
   *      (snap to 0 if the form would land in the awkward 1-12%
   *      sliver band — same rule as drag-release).
   *    - Grows the form back toward the user's preferred percent
   *      when the paper has gotten smaller (e.g. zoom-out).
   *    - No-ops when the user has explicitly collapsed the form
   *      (preferred percent === 0); zoom changes shouldn't reopen
   *      a deliberately-hidden form.
   *  Fires the same onCommit as a drag release so PATCH /me
   *  persistence kicks in. */
  accommodatePaperWidth: (paperRequiredPx: number) => void;
  /** Current percent — useful for read-only checks. */
  getPercent: () => number;
}

const MIN_PERCENT = 12;
const MAX_PERCENT = 80;
/** Drag below this on release → auto-snap to 0 (form fully
 *  collapsed). Picked to match the user's "below 20% feels weird"
 *  intuition — form pane gets too narrow to render meta + raw
 *  input cleanly. */
const COLLAPSE_THRESHOLD = 20;
const COLLAPSE_LEFT = 0;
/** Re-open lands here, not 50, so the editor biases toward the
 *  paper preview by default. Form takes the smaller share. */
const DEFAULT_OPEN_PERCENT = 35;

export const EditorSplitPane = forwardRef<EditorSplitPaneHandle, Props>(
  function EditorSplitPane({ initialPercent, onCommit, left, right }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [percent, setPercent] = useState(() =>
    clampToValid(initialPercent),
  );
  const [isDragging, setIsDragging] = useState(false);
  // Track whether we've already commit-ed this drag — avoids
  // double-commits on the synthetic mouseup that fires after
  // collapse.
  const lastCommittedRef = useRef(percent);

  // The user's *intended* form-pane share. Updated only by user-
  // driven changes (drag mouseup, arrow keys, double-click, re-open
  // tab) — NOT by zoom-driven shrinks. accommodatePaperWidth uses
  // this as the target to grow back toward when the paper has
  // shrunk and there's room. This is what makes "zoom in then
  // zoom out" land back at the size the user originally chose.
  const preferredPercentRef = useRef(clampToValid(initialPercent));

  const commit = useCallback(
    (next: number) => {
      if (next !== lastCommittedRef.current) {
        onCommit(next);
        lastCommittedRef.current = next;
      }
    },
    [onCommit],
  );

  // commitAsPreferred — same as commit, but also updates the
  // user-intent ref so future accommodatePaperWidth calls know to
  // restore back to this size. Use for any user-driven change.
  const commitAsPreferred = useCallback(
    (next: number) => {
      preferredPercentRef.current = next;
      commit(next);
    },
    [commit],
  );

  // Imperative handle for programmatic adjustments (zoom changes
  // → fit the form around the paper). Reads from refs so the
  // closure always sees the latest percent + container width
  // without stale-state risks.
  const percentRef = useRef(percent);
  percentRef.current = percent;
  useImperativeHandle(
    ref,
    () => ({
      accommodatePaperWidth: (paperRequiredPx: number) => {
        const container = containerRef.current;
        if (!container) return;
        const containerWidth = container.clientWidth;
        if (containerWidth <= 0) return;

        // Honor a deliberate collapse: if the user closed the form
        // themselves, zoom changes shouldn't reopen it.
        const preferred = preferredPercentRef.current;
        if (preferred === COLLAPSE_LEFT) return;

        // Right pane gets (100 - formPercent)% of container width.
        // Solve for the largest formPercent that still leaves the
        // paper enough room: p ≤ (1 - paperRequiredPx/container)*100.
        const maxFormPercent = Math.max(
          0,
          (1 - paperRequiredPx / containerWidth) * 100,
        );

        // Target = whichever is smaller of "what the user wants"
        // and "what fits". This makes zoom-in shrink the form,
        // zoom-out grow it back, both via the same rule.
        const target = Math.min(preferred, maxFormPercent);

        // Snap-to-zero rule: if target lands in the awkward
        // 1-12% sliver, collapse the form entirely (matches
        // drag-release behavior).
        const snapped =
          target < MIN_PERCENT ? COLLAPSE_LEFT : Math.round(target);

        if (snapped === percentRef.current) return;
        setPercent(snapped);
        // Use commit (not commitAsPreferred) — this is a layout
        // accommodation, not a user-intent change. preferredPercentRef
        // stays at whatever the user last picked.
        commit(snapped);
      },
      getPercent: () => percentRef.current,
    }),
    [commit],
  );

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDoubleClick = useCallback(() => {
    // Always collapse to paper (0%) on double-click. If already
    // collapsed, re-open to the paper-favored default. This is a
    // user-intent change, so it updates preferredPercentRef.
    setPercent((current) => {
      const next = current === COLLAPSE_LEFT ? DEFAULT_OPEN_PERCENT : COLLAPSE_LEFT;
      commitAsPreferred(next);
      return next;
    });
  }, [commitAsPreferred]);

  // Document-level listeners during drag — keeps the handle from
  // losing the mouse when the cursor moves fast or off the handle.
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const raw = Math.round((x / rect.width) * 100);
      // During drag we let the user sweep all the way down to 0
      // for live preview of the collapse behavior; also clamp at
      // MAX so the form pane can't dominate the paper.
      const next = Math.min(MAX_PERCENT, Math.max(0, raw));
      setPercent(next);
    };
    const onUp = () => {
      setIsDragging(false);
      setPercent((current) => {
        // Snap policy on release:
        //  - <COLLAPSE_THRESHOLD → snap to 0 (form hidden)
        //  - else clamp into [MIN, MAX]
        const next =
          current < COLLAPSE_THRESHOLD
            ? COLLAPSE_LEFT
            : Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, current));
        // User-driven release → update preferredPercentRef so
        // zoom-driven layout changes know to restore back here.
        commitAsPreferred(next);
        return next;
      });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, commitAsPreferred]);

  const collapseLeft = percent === COLLAPSE_LEFT;

  const reopen = () => {
    setPercent(DEFAULT_OPEN_PERCENT);
    // User-intent change — update preferredPercentRef so
    // accommodatePaperWidth grows back here on zoom-out.
    commitAsPreferred(DEFAULT_OPEN_PERCENT);
  };

  return (
    <div
      ref={containerRef}
      className="relative -mx-6 flex flex-col md:flex-row"
      style={{
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      {/* Left pane — flex-basis follows the slider in real time, no
          transition (a transition would cause a visible "flash" /
          delayed layout while dragging). When collapsed, the
          element is hidden entirely so its inner width-dependent
          layout doesn't reflow against a 0px container. */}
      <div
        className={
          'overflow-hidden ' +
          (collapseLeft ? 'hidden md:block md:basis-0' : '')
        }
        style={{
          flexBasis: collapseLeft ? '0%' : `${percent}%`,
        }}
      >
        {left}
      </div>

      {/* Splitter handle — md+ only. Hidden when form is collapsed
          (in that state the re-open tab is the only affordance). */}
      {!collapseLeft && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={MAX_PERCENT}
          tabIndex={0}
          onMouseDown={onMouseDown}
          onDoubleClick={onDoubleClick}
          onKeyDown={(e) => {
            // Arrow keys are user-intent → update preferredPercentRef
            // so zoom-driven layout changes know the new target.
            if (e.key === 'ArrowLeft') {
              const next = Math.max(MIN_PERCENT, percent - 2);
              setPercent(next);
              commitAsPreferred(next);
            } else if (e.key === 'ArrowRight') {
              const next = Math.min(MAX_PERCENT, percent + 2);
              setPercent(next);
              commitAsPreferred(next);
            }
          }}
          title="Drag to resize · Double-click to collapse the form"
          className={
            'group hidden md:flex md:w-2 md:cursor-col-resize md:items-center md:justify-center md:bg-charcoal/5 md:transition-colors md:hover:bg-pine/15 ' +
            (isDragging ? 'md:bg-pine/25' : '')
          }
        >
          <GripVertical
            size={14}
            className={
              'text-charcoal/30 transition-colors ' +
              (isDragging
                ? 'text-pine'
                : 'group-hover:text-pine')
            }
          />
        </div>
      )}

      {/* Right pane (paper) — always present. flex-1 means it
          absorbs whatever the left pane gives back. */}
      <div className="overflow-hidden md:flex-1">{right}</div>

      {/* Re-open tab — fixed to the viewport so it stays vertically
          centered as the user scrolls (was absolute-positioned to
          the parent, which floated mid-page on long docs). Pine
          fill for visibility, taller hit-area, vertical "FORM"
          label running top-to-bottom. */}
      {collapseLeft && (
        <button
          type="button"
          onClick={reopen}
          title="Reopen the form pane"
          aria-label="Reopen form pane"
          className="fixed left-0 top-1/2 z-30 hidden -translate-y-1/2 items-center gap-1.5 rounded-r-2xl border border-pine bg-pine py-8 pl-2 pr-2.5 text-cloud shadow-lg transition-all hover:pl-3 hover:pr-3 hover:shadow-xl md:flex"
        >
          <ChevronRight size={16} />
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            Form
          </span>
        </button>
      )}
    </div>
  );
  },
);

function clampToValid(percent: number): number {
  if (percent === COLLAPSE_LEFT) return percent;
  // Migration default was 50; clamp existing into the new max.
  return Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, percent));
}
